import Animation from 'animation'
import * as d3 from 'd3-selection'
import Uid from 'uid'
import * as shape from 'd3-shape'
import { EPSILON } from 'chart-constants'

/*
 * Parent animation class for all pie animations
 */
class PieAnimation extends Animation {

  /*
   * Used for a `.call` on some transition to fade in all the elements
   *
   * Pass `transitionStroke` true to increase the stroke-width and mask which
   * will expand the slices during the transition.
   */
  fadeIn (selection, styles, transitionStroke = false) {
    selection.style('opacity', 1) // Fade in the `use`

    if (transitionStroke) {
      selection.style('stroke-width', styles.useStrokeWidth)
               .select(d => {
                 d3.select(`#${d.maskUid.id}`)
                   .transition()
                   .duration(selection.duration()) // We can't inherit transitions since they don't have the same parent
                   .delay(selection.delay())
                   .style('stroke-width', styles.maskStrokeWidth)
             })
    }
  }

  /*
   * Fades out the selection and removes the associated `path` and `mask`
   * elements in the `defs` when the transition has finished.
   *
   * Pass `transitionStroke` true to make the stroke-width and mask go to 0,
   * which combines the slices.
   */
  fadeOut (selection, transitionStroke = false) {
    selection.style('opacity', EPSILON)
             .on('end', d => {
               // Remove the mask and path
               d3.selectAll(`#${d.maskUid.id}, #${d.pathUid.id}, #${d.clipUid.id}`)
                 .remove()
             })

    if (transitionStroke) {
      selection.style('stroke-width', '0px')
               .select(d => {
                 d3.select(`#${d.maskUid.id}`)
                   .transition()
                   .duration(selection.duration()) // We can't inherit transitions since they don't have the same parent
                   .delay(selection.delay())
                   .style('stroke-width', '0px')
               })
    }
  }

  /*
   * Adjusts the path's `d` in the defs that's associated with the element
   * in this selection.
   */
  tweenPath (selection, tweener) {
    selection.select(d => {
      let path = d3.select(`#${d.pathUid.id}`)
                   .transition()
                   .duration(selection.duration())
      path.attrTween('d', tweener(path.node(), d.slice))
    })
  }

  /*
   * Adds a clip path for each `use` in the selection (which should be a serie)
   * in the svg defs. This clip path is used to create a smaller representation
   * of the `path` (slice). See #461
   */
  _addAsClipPath (selection) {
    let offset = 1.75
      , defs = d3.select(selection.node().ownerSVGElement).select('defs')
      , arc = shape.arc()
                   .innerRadius(this.styles.innerRadius)
                   .outerRadius(this.styles.outerRadius - offset)

    selection.selectAll('use')
             .select(d => {
               let slice = d3.select(`#${d.pathUid.id}`).node()._current
                 , centroid0 = this.arc.centroid(slice) // Old centroid
                 , centroid1 = arc.centroid(slice) // new centroid with smaller path
                 , diffX = centroid0[0] - centroid1[0]
                 , diffY = centroid0[1] - centroid1[1]

               defs.append('clipPath')
                   .attr('id', d.clipUid.id)
                   .append('path')
                    .attr('transform', `translate(${diffX}, ${diffY})`)
                    .attr('d', arc(slice))
             })
  }

  /*
   * Get the pie for this range id. By default uses the current `pies` bound
   * to the animation. Pass in `pies` to override to a new array of pies, such
   * as the previous array.
   */
  getPie (rangeId, pies = this.pies) {
    if (this.chart.layout.breakdown.calculated) {
      // Only one pie
      return this.pies[0]
    } else {
      return pies.find(pie => pie.rangeId === rangeId)
    }
  }

  bindAndAnimate (container, node) {
    let serie = d3.select(node).datum()
      , defs = d3.select(container.node().ownerSVGElement).select('defs')

    // Grab all the slices for this serie
    let slicesForSerie = this.pies.map((pie, idx) => {
      let slice = pie.slices.find(slice => slice.data.serieId === serie.id)
        , id = `${this.chart.layout.breakdown.calculated ? pie.rangeId + '-' : `${idx}`}${serie.id}`

      return {
        id,
        title: pie.title,
        dimensions: pie.dimensions,
        styles: this.styles,
        subtitle: pie.subtitle,
        rangeId: pie.rangeId,
        clipUid: new Uid(`${pie.chartId}-serie-${id}-${idx}-clip-path`),
        pathUid: new Uid(`${pie.chartId}-serie-${id}-${idx}-path`),
        maskUid: new Uid(`${pie.chartId}-serie-${id}-${idx}-mask`),
        slice
      }
    })

    let use = d3.select(node)
                .selectAll('use')
                .data(slicesForSerie.filter(s => s.slice), d => d.id) // Only draw the slice if there's a value. Impact #40130
      , enter = use.enter()
                   .insert('use', ':first-child')
                   .attr('transform', d => {
                     return `translate(${d.dimensions.x + d.dimensions.width / 2}, ${d.dimensions.y + d.dimensions.height / 2})`
                   })
                   .style('opacity', EPSILON)
                   .select(function (d) {
                     // We need to add the actual drawing for this `use`
                     // The drawing goes in the `defs` since components are
                     // shared

                     // Add the path
                     defs.selectAll(`path.pie-donut#${d.pathUid.id}`)
                         .data(d => [d])
                         .enter()
                         .append('path')
                         .classed('pie-donut', true)
                         .attr('id', d.pathUid.id)

                     // Add a mask
                     defs.selectAll(`mask.pie-donut#${d.maskUid.id}`)
                         .data(d => [d])
                         .enter()
                         .append('mask')
                         .classed('pie-donut', true)
                         .attr('id', d.maskUid.id)
                         .append('use')
                            .attr('xlink:href', d.pathUid.href)

                     return this
                   })
                   .attr('xlink:href', d => d.pathUid.href) // reference the path
                   .attr('mask', d => d.maskUid) // same for the mask
      , transition = use.merge(enter)
                        .attr('class', d => d.slice.data.color)
                        .transition(container)
                        .attr('transform', d => {
                          return `translate(${d.dimensions.x + d.dimensions.width / 2}, ${d.dimensions.y + d.dimensions.height / 2})`
                        })
      , exit = use.exit().transition(container)

    use.exit().select(function (d, i) {
      d3.select(this).remove()
    })
    this.animate(serie, enter, use, transition, exit)
  }

}

export default PieAnimation
